import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ROUTES } from '@/config/routes.ts';
import { organizerSidebar } from '@/config/sidebarConfig.ts';
import {
  getCompetitionApi,
  getCompetitionResultsApi,
  updateCompetitionStatusApi,
  upsertCompetitionResultsApi,
} from '@/lib/api/competitionsApi';
import { fetchAllTeams } from '@/lib/api/teamsApi';
import { queryKeys } from '@/lib/query/queryKeys';
import { formatDateAbbreviatedFr } from '@shared';
import { CompetitionStatus, TeamRegistrationStatus } from '@shared/enums/domain.enum';
import type { Competition, CompetitionResult, Team } from '@shared/types/entities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, CheckCircle2, ClipboardList, Trophy } from 'lucide-react';
import { toast } from 'sonner';

import DashboardLayout from '../../components/layout/DashboardLayout';
import PageHeader from '../../components/ui/PageHeader';
import GestionConcoursImportPanel from './GestionConcoursImportPanel';

// ─── Types internes ───────────────────────────────────────
interface ResultRow {
  teamId: number;
  rank: string; // string pour l'input (vide si non saisi)
  notes: string;
}

// ─── Helpers ──────────────────────────────────────────────
function playerNames(team: Team): string {
  if (!team.players?.length) return '';
  return team.players
    .sort((a, b) => a.playerOrder - b.playerOrder)
    .map(p => `${p.firstName} ${p.lastName}`)
    .join(', ');
}

function validateRows(rows: ResultRow[]): string | null {
  const filled = rows.filter(r => r.rank !== '');
  if (filled.length === 0) return 'Veuillez saisir au moins un rang.';
  for (const r of filled) {
    const n = Number(r.rank);
    if (!Number.isInteger(n) || n < 1) return 'Les rangs doivent être des entiers positifs (≥ 1).';
  }
  const hasRank1 = filled.some(r => Number(r.rank) === 1);
  if (!hasRank1) return 'Le rang 1 (vainqueur) doit être attribué.';
  return null;
}

function buildInitialRows(teams: Team[], existingResults: CompetitionResult[]): ResultRow[] {
  return teams.map(t => {
    const existing = existingResults.find(r => r.teamId === t.id);
    return {
      teamId: t.id,
      rank: existing ? String(existing.rank) : '',
      notes: existing?.notes ?? '',
    };
  });
}

// ─── Formulaire de saisie ─────────────────────────────────
function EntryForm({
  competition,
  teams,
  existingResults,
}: {
  competition: Competition;
  teams: Team[];
  existingResults: CompetitionResult[];
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const competitionId = competition.id;

  // Initialisation lazy depuis les données reçues en props (pas de useEffect)
  const [rows, setRows] = useState<ResultRow[]>(() => buildInitialRows(teams, existingResults));

  const saveMutation = useMutation({
    mutationFn: (rowsToSave: ResultRow[]) => {
      const results = rowsToSave
        .filter(r => r.rank !== '')
        .map(r => ({
          teamId: r.teamId,
          rank: Number(r.rank),
          notes: r.notes || undefined,
        }));
      return upsertCompetitionResultsApi(competitionId, { results });
    },
    onSuccess: () => {
      toast.success('Résultats enregistrés avec succès');
      void queryClient.invalidateQueries({ queryKey: ['competitions', competitionId, 'results'] });
    },
    onError: () => toast.error("Erreur lors de l'enregistrement des résultats"),
  });

  const finishMutation = useMutation({
    mutationFn: () => updateCompetitionStatusApi(competitionId, CompetitionStatus.FINISHED),
    onSuccess: () => {
      toast.success('Concours terminé — résultats publiés');
      void queryClient.invalidateQueries({ queryKey: queryKeys.competitions.all() });
      navigate(ROUTES.COMPETITION_LIST);
    },
    onError: () => toast.error('Erreur lors de la clôture du concours'),
  });

  function handleRankChange(teamId: number, value: string) {
    setRows(prev => prev.map(r => (r.teamId === teamId ? { ...r, rank: value } : r)));
  }

  function handleNotesChange(teamId: number, value: string) {
    setRows(prev => prev.map(r => (r.teamId === teamId ? { ...r, notes: value } : r)));
  }

  function handleSave() {
    const err = validateRows(rows);
    if (err) {
      toast.error(err);
      return;
    }
    saveMutation.mutate(rows);
  }

  function handleFinish() {
    const err = validateRows(rows);
    if (err) {
      toast.error(err);
      return;
    }
    saveMutation.mutate(rows, {
      onSuccess: () => finishMutation.mutate(),
    });
  }

  const filledCount = rows.filter(r => r.rank !== '').length;
  const isFinished = competition.status === CompetitionStatus.FINISHED;
  const competitionDatePassed = competition.date != null && new Date(competition.date) <= new Date();
  const canFinish = !isFinished && filledCount > 0 && competitionDatePassed;

  const sortedRows = [...rows].sort((a, b) => {
    if (a.rank === '' && b.rank === '') return 0;
    if (a.rank === '') return 1;
    if (b.rank === '') return -1;
    return Number(a.rank) - Number(b.rank);
  });

  return (
    <>
      {/* Stat Cards */}
      <div className='mb-8 grid grid-cols-3 gap-4'>
        <div className='rounded-2xl border border-slate-200 bg-white p-5'>
          <p className='text-xs font-semibold uppercase tracking-wider text-slate-500'>Équipes confirmées</p>
          <p className='mt-1 text-3xl font-extrabold text-slate-900'>{teams.length}</p>
        </div>
        <div className='rounded-2xl border border-slate-200 bg-white p-5'>
          <p className='text-xs font-semibold uppercase tracking-wider text-slate-500'>Résultats saisis</p>
          <p className='mt-1 text-3xl font-extrabold text-slate-900'>
            {filledCount} / {rows.length}
          </p>
        </div>
        <div className='rounded-2xl border border-slate-200 bg-white p-5'>
          <p className='text-xs font-semibold uppercase tracking-wider text-slate-500'>Statut du concours</p>
          <p className='mt-1 text-sm font-semibold text-slate-700'>
            {competition.status === CompetitionStatus.FINISHED
              ? '✅ Terminé'
              : competition.status === CompetitionStatus.IN_PROGRESS
                ? '🔵 En cours'
                : '🟠 Inscriptions fermées'}
          </p>
        </div>
      </div>

      {/* Table de saisie */}
      <div className='rounded-2xl border border-slate-200 bg-white'>
        <div className='flex items-center gap-3 border-b border-slate-200 px-6 py-4'>
          <div className='flex h-10 w-10 items-center justify-center rounded-xl bg-violet-100'>
            <ClipboardList className='h-5 w-5 text-violet-600' />
          </div>
          <div>
            <h2 className='text-base font-bold text-slate-900'>Classement final</h2>
            <p className='text-xs text-slate-500'>
              Même rang = ex-æquo. Exemples : 2 finalistes → rang 2 ; 4 demi-finalistes → rang 3 ; 8 quarts → rang 5
            </p>
          </div>
        </div>

        <div className='grid grid-cols-12 gap-4 border-b border-slate-100 px-6 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500'>
          <div className='col-span-1'>Rang</div>
          <div className='col-span-5'>Équipe</div>
          <div className='col-span-4'>Joueurs</div>
          <div className='col-span-2'>Notes</div>
        </div>

        {rows.length === 0 ? (
          <div className='px-6 py-12 text-center text-sm text-slate-500'>Aucune équipe confirmée pour ce concours.</div>
        ) : (
          sortedRows.map(row => {
            const team = teams.find(t => t.id === row.teamId);
            if (!team) return null;
            const isRanked = row.rank !== '';
            return (
              <div
                key={row.teamId}
                className={`grid grid-cols-12 items-center gap-4 border-b border-slate-50 px-6 py-3 transition hover:bg-slate-50 ${
                  isRanked ? '' : 'opacity-60'
                }`}
              >
                <div className='col-span-1'>
                  <input
                    type='number'
                    min={1}
                    value={row.rank}
                    onChange={e => handleRankChange(row.teamId, e.target.value)}
                    placeholder='—'
                    className='w-full rounded-lg border border-slate-200 px-2 py-1.5 text-center text-sm font-bold text-slate-900 focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-200'
                  />
                </div>

                <div className='col-span-5'>
                  <p className='font-semibold text-slate-900'>{team.name}</p>
                  {isRanked && Number(row.rank) === 1 && (
                    <span className='inline-flex items-center gap-1 text-xs font-semibold text-amber-600'>
                      <Trophy className='h-3 w-3' />
                      Vainqueur
                    </span>
                  )}
                </div>

                <div className='col-span-4 text-xs text-slate-500'>{playerNames(team)}</div>

                <div className='col-span-2'>
                  <input
                    type='text'
                    value={row.notes}
                    onChange={e => handleNotesChange(row.teamId, e.target.value)}
                    placeholder='Notes…'
                    className='w-full rounded-lg border border-slate-200 px-2 py-1.5 text-xs text-slate-600 focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-200'
                  />
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Actions */}
      <div className='mt-6 flex items-center justify-between'>
        <button
          onClick={() => navigate(ROUTES.COMPETITION_LIST)}
          className='flex items-center gap-2 text-sm text-slate-500 transition hover:text-slate-700'
        >
          <ArrowLeft className='h-4 w-4' />
          Retour à la liste
        </button>

        <div className='flex items-center gap-3'>
          <button
            onClick={handleSave}
            disabled={saveMutation.isPending || rows.length === 0 || !competitionDatePassed}
            title={!competitionDatePassed ? 'Disponible après la date du concours' : undefined}
            className='flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed'
          >
            {saveMutation.isPending ? 'Enregistrement…' : 'Enregistrer les résultats'}
          </button>

          {canFinish && (
            <button
              onClick={handleFinish}
              disabled={finishMutation.isPending || saveMutation.isPending}
              className='flex items-center gap-2 rounded-xl bg-violet-600 px-5 py-2 text-sm font-bold text-white shadow-lg shadow-violet-200 transition hover:bg-violet-700 disabled:opacity-50'
            >
              <CheckCircle2 className='h-4 w-4' />
              {finishMutation.isPending ? 'Clôture…' : 'Terminer le concours'}
            </button>
          )}

          {isFinished && (
            <div className='flex items-center gap-2 rounded-xl bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-600'>
              <CheckCircle2 className='h-4 w-4 text-emerald-500' />
              Concours terminé
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Shell (loader) ───────────────────────────────────────
export default function CompetitionResultsEntry() {
  const { id } = useParams<{ id: string }>();
  const competitionId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: competition, isLoading: loadingComp } = useQuery({
    queryKey: queryKeys.competitions.detail(competitionId),
    queryFn: () => getCompetitionApi(competitionId),
    enabled: !!competitionId,
  });

  const { data: teamsData, isLoading: loadingTeams } = useQuery({
    queryKey: queryKeys.teams.byCompetition(competitionId),
    queryFn: () =>
      fetchAllTeams({
        competitionId,
        registrationStatus: TeamRegistrationStatus.CONFIRMED,
      }),
    enabled: !!competitionId,
  });

  const { data: existingResults, isLoading: loadingResults } = useQuery({
    queryKey: ['competitions', competitionId, 'results'] as const,
    queryFn: () => getCompetitionResultsApi(competitionId),
    enabled: !!competitionId,
  });

  const isLoading = loadingComp || loadingTeams || loadingResults;

  const subtitle = competition
    ? `${competition.date ? formatDateAbbreviatedFr(competition.date) : ''}${competition.venue ? ` · ${competition.venue}` : ''}${competition.city ? ` · ${competition.city}` : ''}`
    : '';

  return (
    <DashboardLayout sidebarConfig={organizerSidebar}>
      <div className='p-8'>
        <PageHeader
          title={competition ? `Résultats · ${competition.name}` : 'Saisie des résultats'}
          subtitle={subtitle}
          actions={
            <button
              onClick={() => navigate(ROUTES.COMPETITION_LIST)}
              className='flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50'
            >
              <ArrowLeft className='h-4 w-4' />
              Retour à la liste
            </button>
          }
        />

        {isLoading ? (
          <div className='space-y-4'>
            <div className='mb-8 grid grid-cols-3 gap-4'>
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className='h-24 animate-pulse rounded-2xl bg-slate-200' />
              ))}
            </div>
            <div className='h-64 animate-pulse rounded-2xl bg-slate-200' />
          </div>
        ) : competition && teamsData && existingResults ? (
          <>
            {competition.date != null && new Date(competition.date) > new Date() && (
              <div className='mb-6 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4'>
                <AlertTriangle className='mt-0.5 h-5 w-5 shrink-0 text-amber-500' />
                <div>
                  <p className='font-semibold text-amber-800'>Concours pas encore disputé</p>
                  <p className='mt-0.5 text-sm text-amber-700'>
                    La date du concours est le{' '}
                    {new Date(competition.date!).toLocaleDateString('fr-FR', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                    })}
                    . La saisie des résultats et la clôture ne seront disponibles qu&apos;à partir de cette date.
                  </p>
                </div>
              </div>
            )}
            <GestionConcoursImportPanel
              competitionId={competitionId}
              onApplied={() => {
                void queryClient.invalidateQueries({ queryKey: ['competitions', competitionId, 'results'] });
              }}
            />
            <EntryForm
              key={existingResults
                .map(r => `${r.teamId}:${r.rank}`)
                .sort()
                .join('|')}
              competition={competition}
              teams={teamsData}
              existingResults={existingResults}
            />
          </>
        ) : (
          <div className='py-12 text-center text-sm text-slate-500'>Impossible de charger les données.</div>
        )}
      </div>
    </DashboardLayout>
  );
}
